import type { CrmEventEditScope } from '../../../server/queries/utils/crm-event-build-input.ts';
import type { RouterOutputs } from '../../../utils/api.ts';
import { api } from '../../../utils/api.ts';

type DeleteEventInput = {
    scope: CrmEventEditScope;
    occurrenceDate: string;
};

type UseDeleteEventOptions = {
    onSuccess?: () => void;
    onError?: (message?: string) => void;
};

export function useDeleteEvent(
    event: RouterOutputs['platoUser']['query']['events']['getEventById'],
    options: UseDeleteEventOptions = {},
) {
    const apiUtils = api.useUtils();

    const { mutate, ...deleteMutation } = api.platoUser.command.events.delete.useMutation({
        onSuccess: (_, { scope }) => {
            void apiUtils.platoUser.query.events.getList.invalidate();

            if (scope === 'single' && event.recurrenceRule) {
                void apiUtils.platoUser.query.events.getEventById.invalidate({ eventId: event.event_id });
            }

            if (event.customer) {
                void apiUtils.platoUser.query.events.getUpcomingCustomerEvents.invalidate({
                    customerId: event.customer.customerId,
                });
            }

            // Deleting an event detaches its notes (clears their event link), so any
            // note view that surfaces the linkage can be stale.
            void apiUtils.platoUser.query.customerActivity.getFeedList.invalidate();
            void apiUtils.platoUser.query.notes.getNoteById.invalidate();

            options.onSuccess?.();
        },
        onError: (error) => {
            // eslint-disable-next-line @typescript-eslint/no-base-to-string -- there isn't much else we can do here.
            options.onError?.(error instanceof Error ? error.message : String(error));
        },
    });

    return {
        ...deleteMutation,
        mutate: (args: DeleteEventInput) => {
            if (deleteMutation.isPending) {
                return;
            }

            mutate({ eventId: event.event_id, ...args });
        },
    };
}
